<template>
  <section class="part-collections-card mx-1"
           :class="{'expanded-part-collections-card': hasAnExpandedCollection}">
    <section class="part-collections-toolbar is-flex is-justify-content-space-between mb-3">
      <article class="is-flex">
        <button :disabled="isExpandingAll || isSearchMode"
                class="button mr-2"
                @click="$emit('expand-all')">
          {{ $t('expandAll') }}
        </button>
        <button :disabled="isExpandingAll || isSearchMode"
                class="button"
                @click="$emit('collapse-all')">
          {{ $t('collapseAll') }}
        </button>
      </article>
      <div class="control has-icons-left is-pulled-right half-width search-control">
        <input :value="search"
               :placeholder="`${$t('searchContent', { content: $tc('part', 2) })}...`"
               type="text"
               class="input search-input"
               @input="event => $emit('search-input', event)">
        <span class="icon is-left fas fa-search" />
        <span v-if="search" class="search-right-meta">
          <span class="search-results-count has-text-grey">
            {{ searchResultsCount }} {{ $tc('result', searchResultsCount) }}
          </span>
          <button type="button"
                  class="search-clear-button"
                  @click="$emit('clear-search')">
            <i class="fas fa-times" />
          </button>
        </span>
      </div>
    </section>

    <section ref="scrollContainer"
             class="part-collections-list-region pb-2"
             @scroll="$emit('scroll', $event)">
      <div v-for="collection in collections"
           :id="`part-collection-${collection.id}`"
           :key="collection.rowKey">
        <PartCollectionBlock :can-add-all-to-cart="isWidget ? isWidgetCartEnabled : isCartOrErpCartEnabled"
                             :collection="collection"
                             :column-widths="columnWidths"
                             :content-width="contentWidth"
                             :expanded="getExpanded(collection)"
                             :is-expanding-all="isExpandingAll"
                             :is-search-mode="isSearchMode"
                             :is-searching="isSearching"
                             :part-columns="effectivePartColumns"
                             :has-hide-price="hasHidePrice"
                             :is-widget="isWidget"
                             @addToCart="$emit('add-to-cart', $event)"
                             @add-all-to-cart="$emit('add-all-to-cart', $event)"
                             @export-parts="$emit('export-parts', $event)"
                             @toggle="$emit('toggle-collection', collection.id)"
                             @toggle-show-retail-price="$emit('toggle-show-retail-price', collection.id)"
                             @toggle-show-wholesale-price="$emit('toggle-show-wholesale-price', collection.id)"
                             @toggle-show-discount-price="$emit('toggle-show-discount-price', collection.id)"
                             @previewThumbnail="openPartThumbnailPreview"/>
      </div>
    </section>

    <BomAddToCartModal v-if="addToCartModalOpen && !!cartItem"
                       :bom-item="cartItem"
                       :is-widget-cart-enabled="isWidgetCartEnabled"
                       @add-to-cart="$emit('cart-submit', $event)"
                       @cancel="$emit('close-cart-modal')"
                       @close="$emit('close-cart-modal')"/>
    <TwoDModal v-if="partThumbnailPreview"
               data-unit="part-collection-thumbnail-modal"
               :filepath="partThumbnailPreview.filepath"
               :title="partThumbnailPreview.title"
               @close="closePartThumbnailPreview" />
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import PartCollectionBlock from './PartCollectionBlock.vue'
import BomAddToCartModal from '@/components/library/content/asset/viewer/illustrationViewer/bom/BomAddToCartModal.vue'
import TwoDModal from '@/components/library/content/TwoDModal.vue'
import { getPart } from '@/controllers/library/part'
import { useComposableForEsc } from '@/components/common/modal/useComposableForEsc'

import type { DisplayCollection, PartRow } from '@/interfaces/library/partCollection'

const props = defineProps<{
  ownerId?: number
  search: string
  searchResultsCount: number
  isSearching: boolean
  isSearchMode: boolean
  isExpandingAll: boolean
  collections: DisplayCollection[]
  columnWidths: Record<string, number>
  contentWidth: number
  effectivePartColumns: string[]
  isCartOrErpCartEnabled: boolean
  addToCartModalOpen: boolean
  cartItem: unknown
  isWidgetCartEnabled: boolean
  hasHidePrice: boolean
  hasAnExpandedCollection: boolean
  isWidget: boolean
  getExpanded: (collection: DisplayCollection) => boolean
}>()

defineEmits<{
  (e: 'expand-all'): void
  (e: 'collapse-all'): void
  (e: 'search-input', event: Event): void
  (e: 'clear-search'): void
  (e: 'scroll', event: Event): void
  (e: 'toggle-collection', collectionId: number): void
  (e: 'add-to-cart', payload: PartRow): void
  (e: 'add-all-to-cart', payload: PartRow[]): void
  (e: 'export-parts', collectionId: number): void
  (e: 'cart-submit', qty: number): void
  (e: 'close-cart-modal'): void,
  (e: 'toggle-show-retail-price', collectionId: number): void,
  (e: 'toggle-show-wholesale-price', collectionId: number): void,
  (e: 'toggle-show-discount-price', collectionId: number): void
}>()

const scrollContainer = ref<HTMLElement | null>(null)
const partThumbnailPreview = ref<{ filepath: string, title: string } | null>(null)
let previewRequestId = 0

function getPreviewTitle(part: PartRow): string {
  return part.partNumber
    ? `${part.name} (${part.partNumber})`
    : part.name
}

function toAbsoluteUrl(filepath: string): string {
  return new URL(filepath, window.location.origin).href
}

async function openPartThumbnailPreview(part: PartRow) {
  if (!part.thumbnailUrl) return

  const requestId = ++previewRequestId

  try {
    const partDetails = part.thumbnailUrlLarge
      ? part
      : await getPart(part.id)

    if (requestId !== previewRequestId || !partDetails.thumbnailUrlLarge) return

    partThumbnailPreview.value = {
      filepath: toAbsoluteUrl(partDetails.thumbnailUrlLarge),
      title: getPreviewTitle(part)
    }
  } catch {
    // Keep the compact Part Collections view unchanged if preview data cannot be loaded.
  }
}

function closePartThumbnailPreview() {
  previewRequestId++
  partThumbnailPreview.value = null
}

watch(() => props.ownerId, closePartThumbnailPreview)

onBeforeUnmount(() => {
  previewRequestId++
})

useComposableForEsc({
  dismissFunction: closePartThumbnailPreview,
  isActive: computed(() => partThumbnailPreview.value !== null)
})

defineExpose({
  get scrollRef() {
    return scrollContainer.value ?? null
  }
})
</script>

<style scoped>
.part-collections-card {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: max(45vh, 400px);
}

.expanded-part-collections-card {
  max-height: max(66vh, 561px) !important;
}

.part-collections-toolbar {
  flex: 0 0 auto;
}

.part-collections-list-region {
  flex: 1 1 auto;
  min-height: 285px;
  overflow-y: auto;
  overscroll-behavior: contain;
  overflow-anchor: none;
}

.search-control {
  position: relative;
}

.search-input {
  padding-right: 9rem;
}

.search-right-meta {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-results-count {
  font-size: 0.875rem;
  white-space: nowrap;
  pointer-events: none;
}

.search-clear-button {
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: #7a7a7a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.search-clear-button:hover,
.search-clear-button:focus,
.search-clear-button:active {
  color: #363636;
  outline: none;
}

.icon.is-clickable {
  cursor: pointer;
}
</style>
